import axios, { type AxiosError } from 'axios';
import { getApiBaseUrl, getAgentId } from '@/config/api';
import { getWidgetAgentData } from '@/utils/widgetAgent';

export interface IRequestParams {
  ClassName: string;
  Action: string;
  url?: 'URL' | 'URL_NETAPI';
  [key: string]: unknown;
}

export interface ExecuteForCRUDResponse {
  success: boolean;
  dataType: string | null;
  errorMessage: string;
  successMessage: string;
  data: unknown[];
  dataModel: unknown | null;
  total: number;
  dataTable: unknown | null;
  Json: unknown | null;
}

const defaultResponse: ExecuteForCRUDResponse = {
  success: false,
  dataType: null,
  errorMessage: '',
  successMessage: '',
  data: [],
  dataModel: null,
  total: 0,
  dataTable: null,
  Json: null,
};

export const executeForCRUD = async (
  params: IRequestParams
): Promise<ExecuteForCRUDResponse> => {
  let baseURL = getApiBaseUrl();
  const agentId = getAgentId();
  
  // Determinar la URL base según el parámetro url o la URL actual
  const urlType = params.url;
  const isNetApi = urlType === 'URL_NETAPI' || 
                   baseURL.includes('fbnet') || 
                   baseURL.includes('fbnet.kcapis.net');
  
  // fb.grupokc.com.mx NO tiene endpoint para executeForCRUD
  // Según la guía, executeForCRUD solo está disponible en:
  // - kc-frontback.azurewebsites.net/api/exe4crud
  // - fbnet.kcapis.net/api/Exe4CRUD
  // Si la base URL es fb.grupokc.com.mx, redirigir a la URL correcta según el tipo
  if (baseURL.includes('fb.grupokc.com.mx')) {
    if (urlType === 'URL_NETAPI' || isNetApi) {
      baseURL = 'https://fbnet.kcapis.net';
    } else {
      baseURL = 'https://kc-frontback.azurewebsites.net';
    }
  }
  
  // Si se especifica URL_NETAPI, usar el dominio de NETAPI
  if (urlType === 'URL_NETAPI') {
    if (!baseURL.includes('fbnet')) {
      baseURL = baseURL.replace('fb.grupokc.com.mx', 'fbnet.kcapis.net')
                       .replace('fb.kcapis.net', 'fbnet.kcapis.net')
                       .replace('kc-frontback.azurewebsites.net', 'fbnet.kcapis.net');
    }
  } else if (urlType === 'URL') {
    // Asegurar que sea la URL principal
    if (!baseURL.includes('grupokc') && !baseURL.includes('fb.kcapis.net') && !baseURL.includes('kc-frontback')) {
      baseURL = 'https://kc-frontback.azurewebsites.net';
    }
  }
  
  // Determinar el endpoint según la URL base
  // Según la guía: URL normal usa exe4crud, URL_NETAPI usa Exe4CRUD
  const needsUppercase = isNetApi || baseURL.includes('fbnet');
  const endpoint = needsUppercase ? 'Exe4CRUD' : 'exe4crud';
  const url = `${baseURL}/api/${endpoint}`;
  
  // Remover el parámetro url de los params antes de enviar
  const requestParams = { ...params };
  delete requestParams.url;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Agregar agentId en headers si está disponible
  if (agentId) {
    headers['X-Agent-Id'] = agentId;
  }

  const config = {
    method: 'post',
    url,
    headers,
    data: requestParams,
  };

  try {
    const response = await axios.request<ExecuteForCRUDResponse>(config);
    return response.data;
  } catch (error) {
    console.error('executeForCRUD - Error:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      return {
        ...defaultResponse,
        errorMessage:
          axiosError.message || axiosError.response?.statusText || 'Error desconocido',
      };
    }
    return {
      ...defaultResponse,
      errorMessage: 'Error desconocido',
    };
  }
};

export interface GetComboResponse {
  success: boolean;
  data: Array<{ Key: string; Value: string }>;
  errorMessage?: string;
}

/**
 * Ejecuta peticiones al endpoint de CRM para cotizaciones
 * Usa el endpoint estándar de exe4crud pero con URL específica para CRM
 * @param params Parámetros de la petición
 * @returns Respuesta de la API
 */
export const executeForCRM = async (
  params: IRequestParams
): Promise<ExecuteForCRUDResponse> => {
  // Según la documentación, usar kc-frontback.azurewebsites.net/api/exe4crud
  const baseURL = 'https://kc-frontback.azurewebsites.net';
  const url = `${baseURL}/api/exe4crud`;
  
  // Remover el parámetro url de los params antes de enviar
  const requestParams = { ...params };
  delete requestParams.url;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With',
  };

  const agentId = getAgentId();
  if (agentId) {
    headers['X-Agent-Id'] = agentId;
  }

  const config = {
    method: 'post',
    url,
    headers,
    data: requestParams,
  };

  try {
    const response = await axios.request<unknown[] | ExecuteForCRUDResponse>(config);
    
    // El response puede venir como array donde el índice 1 contiene los datos
    // o directamente como objeto ExecuteForCRUDResponse
    let responseData: ExecuteForCRUDResponse;
    
    if (Array.isArray(response.data)) {
      // Si es array, buscar el objeto con success en el índice 1
      const dataItem = response.data[1];
      if (dataItem && typeof dataItem === 'object' && 'success' in dataItem) {
        responseData = dataItem as ExecuteForCRUDResponse;
      } else {
        // Si no está en el índice 1, buscar en todo el array
        const found = response.data.find(
          (item) => item && typeof item === 'object' && 'success' in item
        ) as ExecuteForCRUDResponse | undefined;
        responseData = found || defaultResponse;
      }
    } else if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      responseData = response.data as ExecuteForCRUDResponse;
    } else {
      responseData = defaultResponse;
    }
    
    return responseData;
  } catch (error) {
    console.error('executeForCRM - Error:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      return {
        ...defaultResponse,
        errorMessage:
          axiosError.message || axiosError.response?.statusText || 'Error desconocido',
      };
    }
    return {
      ...defaultResponse,
      errorMessage: 'Error desconocido',
    };
  }
};

/**
 * Obtiene datos para combos usando el endpoint de combo del backend
 * @param className Nombre de la clase/tabla
 * @param qsParams Query string para filtros (ej: "Id_Zona=1&Activo=true") o string JSON con array de objetos
 * @param sort Ordenamiento (ej: "Nombre ASC") o string JSON con objeto {property, direction}
 * @param urlType Tipo de URL a usar: 'URL' o 'URL_NETAPI'
 * @returns Respuesta con array de { Key, Value }
 */
export const GetCombo = async (
  className: string,
  qsParams?: string,
  sort?: string,
  urlType: 'URL' | 'URL_NETAPI' = 'URL'
): Promise<GetComboResponse> => {
  // Determinar la URL base según el tipo
  let baseURL: string;
  
  // Si se especifica URL_NETAPI, usar el dominio de NETAPI
  if (urlType === 'URL_NETAPI') {
    baseURL = 'https://fbnet.kcapis.net';
  } else {
    // Siempre usar fb.grupokc.com.mx para GetCombo (según requerimiento)
      baseURL = 'https://fb.grupokc.com.mx';
  }
  
  const url = `${baseURL}/api/nf_data/GetCombo`;

  const agentData = getWidgetAgentData();

  const headers: Record<string, string> = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  const formData = new URLSearchParams();
  formData.append('Id_Oficina', String(agentData.Id_Oficina));
  formData.append('Id_Usuario', String(agentData.Id_Usuario));
  formData.append('Id_Usuario_Add', String(agentData.Id_Usuario));
  formData.append('UsuarioAdd', String(agentData.UsuarioAdd));
  formData.append('UsuarioDel', String(agentData.UsuarioDel));
  formData.append('user', String(agentData.user));
  formData.append('Id_Usuario_Modificacion', String(agentData.Id_Usuario_Modificacion));
  formData.append('UsuarioUMod', String(agentData.UsuarioUMod));
  
  // Agregar agentId en el body en lugar del header para evitar problemas de CORS
  const agentId = getAgentId();
  if (agentId) {
    formData.append('AgentId', agentId);
  }
  
  // Campos del combo
  formData.append('className', className);
  formData.append('ComboName', className);
  formData.append('action', 'combo');
  formData.append('action_path', '/nf_data/GetCombo');
  formData.append('db', 'Autonet');
  formData.append('id', '0');

  // Agregar qs si se proporciona (JSON string)
  if (qsParams) {
    formData.append('qs', qsParams);
  }

  // Agregar sort si se proporciona (JSON string)
  if (sort) {
    formData.append('sort', sort);
  }

  const config = {
    method: 'post',
    url,
    headers,
    data: formData.toString(), // Enviar como string form-urlencoded
  };

  try {
    const response = await axios.request<{ success: boolean; data: Array<{ Key: string; Value: string }>; errorMessage?: string }>(config);
    
    if (response.data.success && Array.isArray(response.data.data)) {
      return {
        success: true,
        data: response.data.data,
      };
    }
    
    return {
      success: false,
      data: [],
      errorMessage: response.data.errorMessage || 'Error al obtener el combo',
    };
  } catch (error) {
    console.error('GetCombo - Error:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      return {
        success: false,
        data: [],
        errorMessage: axiosError.message || axiosError.response?.statusText || 'Error desconocido',
      };
    }
    return {
      success: false,
      data: [],
      errorMessage: 'Error desconocido',
    };
  }
};


